<!-- eslint-disable @typescript-eslint/ban-types -->
<template>
  <div class="fixed inset-0 z-[5000] flex items-center justify-center h-full">
    <div class="fixed inset-0 z-[1] bg-black/33 outside-clicker" />
    <div class="min-w-[240px] z-[1]">
      <div
        class="pt-5 pl-5 pr-5 sticky top-0 left-0 z-[1] bg-white dark:bg-gray-800 border-b border-[#ddd] dialog-top rounded-md flex flex-col overflow-hidden"
        :style="{
          'max-width': maxsize + 'px',
          'max-height': computedMaxHeight,
          'min-width': minsize + 'px',
          opacity: modalOpacity,
          transform: modalTransform,
          transition: modalTransition,
        }"
      >
        <div class="text-h6 shrink-0">{{ title }}</div>
        <div
          @close="(value: boolean) => emit('close', value)"
          class="pt-1.5 flex-1 min-h-0 overflow-y-auto"
        >
          <slot />
        </div>
        <dtpCardActions
          :align="actionsAlign"
          class="shrink-0 left-0 min-h-[55px] bg-white border-t border-[#ddd] overflow-hidden dialog-bottom"
        >
          <slot name="footer" />
        </dtpCardActions>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, computed } from "vue";
import {
  useMorphAnimation,
  type MorphAnimationOptions,
} from "@/composables/useMorphAnimation";
import { useFeatureFlags } from "@/composables/useFeatureFlags";

type ActionsAlign =
  | "left"
  | "center"
  | "right"
  | "between"
  | "around"
  | "stretch";

defineOptions({ name: "IstModal" });

const { isEnabled } = useFeatureFlags();
const isIstModalEnabled = computed(() => isEnabled("IstModal"));

const props = withDefaults(
  defineProps<{
    title?: string;
    breakpoint?: number;
    maxsize?: number | string;
    minsize?: number | string;
    maxheight?: number | string | undefined;
    overlay?: boolean;
    urlpath?: string | undefined;
    actionsAlign?: ActionsAlign;
    escapeCallback?: (() => void) | null;
    enterCallback?: (() => void) | null;
    morphSourceElement?: HTMLElement;
    morphAnimationOptions?: MorphAnimationOptions;
  }>(),
  {
    title: "",
    breakpoint: 767,
    maxsize: 1024,
    minsize: 300,
    maxheight: "97vh",
    overlay: false,
    urlpath: undefined,
    actionsAlign: "right",
    escapeCallback: null,
    enterCallback: null,
    morphSourceElement: undefined,
    morphAnimationOptions: undefined,
  },
);

const emit = defineEmits<{
  (e: "close", value: boolean): void;
  (e: "onShortcutSave", value: boolean): void;
  (e: "click", event: MouseEvent): void;
}>();

// Computed style values that handle both numeric and string values (like calc())
const computedMaxHeight = computed(() => {
  if (props.maxheight === undefined) return undefined;
  if (typeof props.maxheight === "string" && isNaN(Number(props.maxheight))) {
    return props.maxheight; // Already has units (e.g., calc(), 100vh, etc.)
  }
  return props.maxheight + "px";
});

// Morph animation setup
const { morphToModal, morphFromModal, resetModalStyles } = useMorphAnimation();
const modalContainer = ref<any>();

// Modal animation control
const modalOpacity = ref<string>("1");
const modalTransform = ref<string>("none");
const modalTransition = ref<string>("none");

// Helper function to get the actual DOM element
const getModalElement = (): HTMLElement | null => {
  if (!modalContainer.value) return null;

  // If it's a Vue component instance, get the $el property
  if (modalContainer.value.$el) {
    return modalContainer.value.$el as HTMLElement;
  }

  // If it's already a DOM element, return it directly
  if (modalContainer.value instanceof HTMLElement) {
    return modalContainer.value;
  }

  return null;
};

const closeDialog = async () => {
  // Handle reverse morph animation if source element is provided
  if (props.morphSourceElement) {
    const modalElement = getModalElement();
    if (modalElement && modalElement.style) {
      // Prepare modal for closing animation - remove size constraints and overflow
      modalElement.classList.add("morphing-close");

      // Disable modal transitions during morph
      modalTransition.value = "none";

      // Run the reverse morph animation (this will create a clone and animate it)
      await morphFromModal(
        modalElement,
        props.morphSourceElement,
        props.morphAnimationOptions,
      );

      // Hide the original modal after morph completes
      modalOpacity.value = "0";
    }
  }

  // Reset modal styles - check if element is still valid
  const modalElement = getModalElement();
  if (modalElement && modalElement.style) {
    resetModalStyles(modalElement);
  }
};

const escapePressed = () => {
  emit("close", false);
};

const onShortcutSave = () => {
  emit("onShortcutSave", false);
};

const MODAL_HISTORY_KEY = "__istModal";
let historyStateManaged = false;

const historypushState = () => {
  history.pushState(
    { [MODAL_HISTORY_KEY]: true },
    document.title,
    location.href,
  );
};

const handleKeyEvents = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    if (typeof props.escapeCallback === "function") {
      props.escapeCallback();
    }
    escapePressed();
  }

  if (event.key === "s" && (event.ctrlKey || event.metaKey)) {
    event.preventDefault();
    if (typeof props.enterCallback === "function") {
      props.enterCallback();
    }
    onShortcutSave();
  }
};

onMounted(async () => {
  // Handle morph animation if source element is provided
  if (props.morphSourceElement) {
    // Hide modal initially for morph animation
    modalOpacity.value = "0";
    modalTransform.value = "scale(1)"; // Keep at final size
    modalTransition.value = "none";

    const modalElement = getModalElement();
    if (modalElement) {
      // Start showing white background during morph
      setTimeout(() => {
        modalOpacity.value = "0.95"; // Show background
        modalTransition.value = "opacity 0.12s ease-out";
      }, 80); // Show background after 80ms

      // Keep modal hidden during morph (no separate zoom animation)
      modalTransform.value = "scale(1)"; // Final size, no overshoot
      modalTransition.value = "none"; // No separate transition

      await morphToModal(
        props.morphSourceElement,
        modalElement,
        props.morphAnimationOptions,
      );

      // Simply fade in the final modal (no zoom animation)
      modalOpacity.value = "1";
      modalTransition.value = "opacity 0.15s ease-out";
    }
  }

  history.pushState(
    { [MODAL_HISTORY_KEY]: true },
    document.title,
    location.href,
  );
  historyStateManaged = true;
  window.addEventListener("popstate", historypushState);
  window.addEventListener("keydown", handleKeyEvents);
});

onBeforeUnmount(async () => {
  await closeDialog();
  window.removeEventListener("keydown", handleKeyEvents);
  window.removeEventListener("popstate", historypushState);
  if (historyStateManaged && history.state?.[MODAL_HISTORY_KEY]) {
    history.back();
  }
});
</script>

<style lang="scss">
@import "../assets/variables";

// Keep morphing-close styles with !important overrides
.morphing-close {
  min-width: unset !important;
  min-height: unset !important;
  overflow: hidden !important;
  max-height: unset !important;
  max-width: unset !important;
}

// Keep styles for nested elements that may not be easily converted
.dialog-content header {
  margin: 10px 0 26px;
  font-size: 18px;
  font-weight: 700;
}

.dialog-content .el-button,
.dialog-content p {
  font-size: inherit;
}

.dialog-content p {
  white-space: pre-wrap;
}

.dialog-content .el-button {
  -ms-flex: 1;
  flex: 1;
  border: 0;
  color: #409eff;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.dialog-content footer {
  display: -ms-flexbox;
  display: flex;
  margin-top: 24px;
}

.dialogfooter {
  min-height: 55px;
}

@media (max-width: 500px) {
  .message-box {
    height: -webkit-fill-available;
  }
}
</style>
